import type { UserProfile } from '@athwab/shared';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthSnapshot {
  status: AuthStatus;
  user: UserProfile | null;
  error?: string;
  errorCode?: string;
}

type Listener = (snapshot: AuthSnapshot) => void;
type Resolver = (snapshot: AuthSnapshot) => void;

class AuthManager {
  private snapshot: AuthSnapshot = { status: 'loading', user: null };
  private listeners = new Set<Listener>();
  private waiters: Resolver[] = [];

  subscribe = (listener: Listener) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = () => this.snapshot;

  setSnapshot = (snapshot: AuthSnapshot) => {
    this.snapshot = snapshot;
    this.listeners.forEach((listener) => listener(this.snapshot));

    if (this.snapshot.status !== 'loading') {
      this.waiters.forEach((resolve) => resolve(this.snapshot));
      this.waiters = [];
    }
  };

  waitForResolved = async (): Promise<AuthSnapshot> => {
    if (this.snapshot.status !== 'loading') {
      return this.snapshot;
    }

    return new Promise<AuthSnapshot>((resolve) => {
      this.waiters.push(resolve);
    });
  };
}

export const authManager = new AuthManager();
